// The pricing strategy entry form, lifted out of PricingStrategySettings so the budget
// screen can open the SAME editor instead of sending the user off to Settings and back.
//
// Nothing about the form changed in the move except two additions that help both call
// sites: an `intro` slot for context about why the strategy is being created, and a
// warning when a strategy has no components (which is a strategy that prices nothing -
// see hasPricingComponents).
import { Alert, Button } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { CommodityManagementApi } from "../../../../Apis/Apis";
import {
  BudgetPricingComponentModel,
  BudgetPricingStrategyModel,
  ComponentType,
  ElectricityType,
  GetCustomerDataResponse,
  GetCustomerSettingsResponse,
  HedgeSource,
  MarketPriceProvider,
  PositionPricingComponentModel,
  PositionPricingStrategyModel,
  UpdateBudgetPricingComponentModel,
  UpdateBudgetPricingStrategyModel,
  UpdatePositionPricingComponentModel,
  UpdatePositionPricingStrategyModel,
  UpdatePricingStrategiesRequest,
  Unit,
} from "../../../../Generated/Commodity-Management-Api";
import { MarketViewProductModel } from "../../../../Generated/Mark-It-View-Api";
import { StoneXRow } from "../../../StoneX";
import { StoneXAutocomplete, StoneXButton, StoneXSelect, StoneXTextField } from "../../../StoneXMui";

export function hedgeSourceLabel(source: HedgeSource) {
  switch (source) {
    case HedgeSource.Positions:
      return "From Positions";
    case HedgeSource.AlwaysFullyHedged:
      return "Always Fully Hedged";
    case HedgeSource.MatchAllInHedge:
      return "Match All-In Hedge";
    default:
      return source;
  }
}

interface ConversionValues {
  bushelUnit?: Unit;
  bushelVolume?: number | null;
  contractUnit?: Unit;
  contractVolume?: number | null;
}

interface StrategyScopeFieldsProps {
  customerData: GetCustomerDataResponse;
  commodityId: number | null | undefined;
  locationId: number | null | undefined;
  counterpartyId: number | null | undefined;
  showLocation: boolean;
  showCounterparty: boolean;
  onCommodityChange: (commodityId: number | null) => void;
  onLocationChange: (locationId: number | null) => void;
  onCounterpartyChange: (counterpartyId: number | null) => void;
}

interface PricingComponentEditorProps {
  component: BudgetPricingComponentModel | PositionPricingComponentModel;
  marketViewProducts: MarketViewProductModel[];
  unitOptions: Unit[];
  showSettlementField: boolean;
  showHedgeSource: boolean;
  removeLabel: string;
  onChange: (patch: Partial<BudgetPricingComponentModel | PositionPricingComponentModel>) => void;
  onRemove: () => void;
}

interface ConversionFieldsProps {
  values: ConversionValues;
  unitOptions: Unit[];
  onChange: (patch: Partial<ConversionValues>) => void;
}

function ConversionFields(props: ConversionFieldsProps) {
  const { values, unitOptions, onChange } = props;

  const hasExistingValues =
    values.bushelUnit !== undefined ||
    values.bushelVolume !== null && values.bushelVolume !== undefined ||
    values.contractUnit !== undefined ||
    values.contractVolume !== null && values.contractVolume !== undefined;

  const [showConversions, setShowConversions] = useState<boolean>(hasExistingValues);

  useEffect(() => {
    if (hasExistingValues) {
      setShowConversions(true);
    }
  }, [hasExistingValues]);

  if (!showConversions) {
    return (
      <StoneXRow justify="start">
        <Button variant="text" onClick={() => setShowConversions(true)}>
          Add bushel / contract conversions
        </Button>
      </StoneXRow>
    );
  }

  return (
    <>
      <StoneXSelect
        label="Bushel Unit"
        options={unitOptions}
        getId={(x) => x}
        getOptionLabel={(x) => x}
        value={values.bushelUnit ?? null}
        onChange={(value) => onChange({ bushelUnit: value ?? undefined })}
        clearable
        fullWidth
      />

      <br />
      <br />

      <StoneXTextField
        type="number"
        label="Bushel Volume"
        value={values.bushelVolume?.toString() ?? ""}
        onUpdate={(value) => onChange({ bushelVolume: value ? Number(value) : null })}
        fullWidth
      />

      <br />
      <br />

      <StoneXSelect
        label="Contract Unit"
        options={unitOptions}
        getId={(x) => x}
        getOptionLabel={(x) => x}
        value={values.contractUnit ?? null}
        onChange={(value) => onChange({ contractUnit: value ?? undefined })}
        clearable
        fullWidth
      />

      <br />
      <br />

      <StoneXTextField
        type="number"
        label="Contract Volume"
        value={values.contractVolume?.toString() ?? ""}
        onUpdate={(value) => onChange({ contractVolume: value ? Number(value) : null })}
        fullWidth
      />

      <br />
      <br />
    </>
  );
}

function StrategyScopeFields(props: StrategyScopeFieldsProps) {
  const {
    customerData,
    commodityId,
    locationId,
    counterpartyId,
    showLocation,
    showCounterparty,
    onCommodityChange,
    onLocationChange,
    onCounterpartyChange,
  } = props;

  return (
    <>
      <StoneXSelect
        label="Commodity"
        options={customerData.commodities ?? []}
        getId={(x) => x.commodityId}
        getOptionLabel={(x) => x.commodityName}
        value={(customerData.commodities ?? []).find((x) => x.commodityId === commodityId) ?? null}
        onChange={(value) => onCommodityChange(value?.commodityId ?? null)}
        clearable
        fullWidth
        placeholder="Applies to all commodities"
      />

      <br />
      <br />

      {showLocation && (
        <>
          <StoneXSelect
            label="Location"
            options={customerData.locations ?? []}
            getId={(x) => x.locationId}
            getOptionLabel={(x) => x.locationName}
            value={(customerData.locations ?? []).find((x) => x.locationId === locationId) ?? null}
            onChange={(value) => onLocationChange(value?.locationId ?? null)}
            clearable
            fullWidth
            placeholder="Applies to all locations"
          />

          <br />
          <br />
        </>
      )}

      {showCounterparty && (
        <>
          <StoneXSelect
            label="Counterparty"
            options={customerData.counterparties ?? []}
            getId={(x) => x.counterpartyId}
            getOptionLabel={(x) => x.counterpartyName}
            value={(customerData.counterparties ?? []).find((x) => x.counterpartyId === counterpartyId) ?? null}
            onChange={(value) => onCounterpartyChange(value?.counterpartyId ?? null)}
            clearable
            fullWidth
            placeholder="Applies to all counterparties"
          />

          <br />
          <br />
        </>
      )}
    </>
  );
}

function PricingComponentEditor(props: PricingComponentEditorProps) {
  const { component, marketViewProducts, unitOptions, showSettlementField, showHedgeSource, removeLabel, onChange, onRemove } = props;
  const positionComponent = component as PositionPricingComponentModel;
  const budgetComponent = component as BudgetPricingComponentModel;

  return (
    <div className="card">
      <StoneXSelect
        label="Component Type"
        options={Object.values(ComponentType)}
        getId={(x) => x}
        getOptionLabel={(x) => x}
        value={component.componentType}
        onChange={(value) => onChange({ componentType: value ?? ComponentType.None })}
        fullWidth
      />

      <br />
      <br />

      <StoneXSelect
        label="Market Price Provider"
        options={Object.values(MarketPriceProvider)}
        getId={(x) => x}
        getOptionLabel={(x) => x}
        value={component.marketPriceProvider ?? null}
        onChange={(value) => {
          const nextProvider = value ?? undefined;
          onChange({
            marketPriceProvider: nextProvider,
            marketViewProductId: nextProvider === MarketPriceProvider.MarketView ? component.marketViewProductId ?? null : null,
          });
        }}
        fullWidth
        clearable
      />

      <br />
      <br />

      {component.marketPriceProvider === MarketPriceProvider.MarketView && (
        <>
          <StoneXAutocomplete
            label="MarketView Product"
            options={marketViewProducts}
            getOptionLabel={(x) => x.name}
            getOptionKey={(x) => x.productId}
            value={marketViewProducts.find((x) => x.productId === component.marketViewProductId) ?? null}
            onUpdate={(value) => onChange({ marketViewProductId: value?.productId ?? null })}
            isOptionEqualToValue={(option, value) => option.productId === value.productId}
            width="100%"
          />

          <br />
          <br />
        </>
      )}

      {showHedgeSource && (
        <>
          <StoneXSelect
            label="Hedge Source"
            options={Object.values(HedgeSource)}
            getId={(x) => x}
            getOptionLabel={(x) => hedgeSourceLabel(x)}
            value={budgetComponent.hedgeSource ?? HedgeSource.Positions}
            onChange={(value) => onChange({ hedgeSource: value ?? HedgeSource.Positions } as Partial<BudgetPricingComponentModel>)}
            fullWidth
          />

          <br />
          <br />
        </>
      )}

      {showSettlementField && (
        <>
          <StoneXTextField
            label="Settlement Symbol"
            value={positionComponent.settlementSymbol ?? ""}
            onUpdate={(value) => onChange({ settlementSymbol: value || null })}
            fullWidth
          />

          <br />
          <br />

          <ConversionFields
            values={{
              bushelUnit: positionComponent.bushelUnit,
              bushelVolume: positionComponent.bushelVolume,
              contractUnit: positionComponent.contractUnit,
              contractVolume: positionComponent.contractVolume,
            }}
            unitOptions={unitOptions}
            onChange={(patch) => onChange(patch)}
          />
        </>
      )}

      <Button variant="text" onClick={onRemove}>{removeLabel}</Button>
    </div>
  );
}

/**
 * A blank strategy. `defaults` is how the budget screen seeds the commodity and
 * counterparty it is entering budgets for - note it deliberately does NOT seed the
 * location, which stays null so the strategy covers every location unless the user
 * decides otherwise.
 */
export function createEmptyBudgetPricingStrategy(
  defaults?: Partial<BudgetPricingStrategyModel>,
): BudgetPricingStrategyModel {
  return {
    budgetPricingStrategyId: 0,
    name: "",
    commodityId: null,
    locationId: null,
    counterpartyId: null,
    isLastDay: false,
    components: [],
    bushelUnit: undefined,
    bushelVolume: null,
    contractUnit: undefined,
    contractVolume: null,
    electricityType: undefined,
    positionStrategies: [],
    ...defaults,
  } as BudgetPricingStrategyModel;
}

/** A single all-in price component - the simplest usable strategy to start from. */
export function createDefaultBudgetPricingComponent(): BudgetPricingComponentModel {
  return {
    budgetPricingComponentId: 1,
    budgetPricingStrategyId: 0,
    componentType: ComponentType.AllIn,
    marketPriceProvider: undefined,
    marketViewProductId: null,
    hedgeSource: HedgeSource.Positions,
  };
}

function toUpdateBudgetPricingComponent(component: BudgetPricingComponentModel): UpdateBudgetPricingComponentModel {
  return {
    budgetPricingComponentId: component.budgetPricingComponentId,
    componentType: component.componentType,
    marketPriceProvider: component.marketPriceProvider,
    marketViewProductId: component.marketViewProductId ?? null,
    hedgeSource: component.hedgeSource ?? HedgeSource.Positions,
  };
}

function toUpdatePositionPricingComponent(component: PositionPricingComponentModel): UpdatePositionPricingComponentModel {
  return {
    positionPricingComponentId: component.positionPricingComponentId,
    componentType: component.componentType,
    marketPriceProvider: component.marketPriceProvider,
    marketViewProductId: component.marketViewProductId ?? null,
    bushelUnit: component.bushelUnit,
    bushelVolume: component.bushelVolume ?? null,
    contractUnit: component.contractUnit,
    contractVolume: component.contractVolume ?? null,
    settlementSymbol: component.settlementSymbol ?? null,
  };
}

function toUpdatePositionPricingStrategy(strategy: PositionPricingStrategyModel): UpdatePositionPricingStrategyModel {
  return {
    positionPricingStrategyId: strategy.positionPricingStrategyId,
    name: strategy.name,
    commodityId: strategy.commodityId ?? null,
    locationId: strategy.locationId ?? null,
    counterpartyId: strategy.counterpartyId ?? null,
    electricityType: strategy.electricityType ?? undefined,
    components: (strategy.components ?? []).map(toUpdatePositionPricingComponent),
  };
}

export function toUpdateBudgetPricingStrategy(strategy: BudgetPricingStrategyModel): UpdateBudgetPricingStrategyModel {
  return {
    budgetPricingStrategyId: strategy.budgetPricingStrategyId,
    name: strategy.name,
    commodityId: strategy.commodityId ?? null,
    locationId: strategy.locationId ?? null,
    counterpartyId: strategy.counterpartyId ?? null,
    bushelUnit: strategy.bushelUnit,
    bushelVolume: strategy.bushelVolume ?? null,
    contractUnit: strategy.contractUnit,
    contractVolume: strategy.contractVolume ?? null,
    electricityType: strategy.electricityType ?? undefined,
    components: (strategy.components ?? []).map(toUpdateBudgetPricingComponent),
    positionStrategies: (strategy.positionStrategies ?? []).map(toUpdatePositionPricingStrategy),
  };
}

/**
 * Persist a strategy and return it as the server stored it (with real ids). Shared so
 * the budget screen writes strategies exactly the way Settings does.
 */
export async function savePricingStrategy(strategy: BudgetPricingStrategyModel): Promise<BudgetPricingStrategyModel> {
  const normalizedStrategy: BudgetPricingStrategyModel = {
    ...strategy,
    positionStrategies: strategy.positionStrategies?.map((positionStrategy) => ({
      ...positionStrategy,
      budgetPricingStrategyId: strategy.budgetPricingStrategyId,
    })),
  };

  const payload: UpdatePricingStrategiesRequest = {
    pricingStrategy: toUpdateBudgetPricingStrategy(normalizedStrategy),
  };

  const response = await CommodityManagementApi.updatePricingStrategies(undefined, payload);
  return response.data.pricingStrategy!;
}

export interface PricingStrategyEditorProps {
  strategy: BudgetPricingStrategyModel;
  customerData: GetCustomerDataResponse;
  customerSettings: GetCustomerSettingsResponse;
  marketViewProducts: MarketViewProductModel[];
  usesSeperatePricingStrategies: boolean;
  /** Context shown above the form, e.g. which budget entry prompted this. */
  intro?: ReactNode;
  onChange: (strategy: BudgetPricingStrategyModel) => void;
  onCancel: () => void;
  onSave: (strategy: BudgetPricingStrategyModel) => Promise<void>;
  isWorking: boolean;
}

export default function PricingStrategyEditor(props: PricingStrategyEditorProps) {
  const {
    strategy,
    customerData,
    customerSettings,
    marketViewProducts,
    usesSeperatePricingStrategies,
    intro,
    onChange,
    onCancel,
    onSave,
    isWorking,
  } = props;

  const budgetByLocation = customerSettings?.budgetsByLocation ?? false;
  const budgetByCounterparty = customerSettings?.budgetsByCounterparty ?? false;
  const positionByLocation = customerSettings?.positionsByLocation ?? false;
  const positionByCounterparty = customerSettings?.positionsByCounterparty ?? false;

  const hasComponents = (strategy.components ?? []).length > 0;

  function updateStrategy(patch: Partial<BudgetPricingStrategyModel>) {
    onChange({ ...strategy, ...patch });
  }

  function updateComponent(componentIndex: number, patch: Partial<BudgetPricingComponentModel>) {
    const updatedComponents = [...(strategy.components ?? [])];
    updatedComponents[componentIndex] = {
      ...updatedComponents[componentIndex],
      ...patch,
    };

    updateStrategy({ components: updatedComponents });
  }

  function addComponent() {
    const nextComponentId = (strategy.components ?? []).reduce((max, component) => Math.max(max, component.budgetPricingComponentId), 0) + 1;

    const nextComponent: BudgetPricingComponentModel = {
      budgetPricingComponentId: nextComponentId,
      budgetPricingStrategyId: strategy.budgetPricingStrategyId,
      componentType: ComponentType.None,
      marketPriceProvider: undefined,
      marketViewProductId: null,
      hedgeSource: HedgeSource.Positions,
    };

    updateStrategy({ components: [...(strategy.components ?? []), nextComponent] });
  }

  function removeComponent(componentIndex: number) {
    updateStrategy({
      components: (strategy.components ?? []).filter((_, idx) => idx !== componentIndex),
    });
  }

  function updatePositionStrategies(nextPositionStrategies: PositionPricingStrategyModel[]) {
    updateStrategy({ positionStrategies: nextPositionStrategies });
  }

  function createEmptyPositionStrategy() {
    const nextPositionStrategyId =
      (strategy.positionStrategies ?? []).reduce((max, positionStrategy) => Math.max(max, positionStrategy.positionPricingStrategyId), 0) + 1;

    return {
      positionPricingStrategyId: nextPositionStrategyId,
      budgetPricingStrategyId: strategy.budgetPricingStrategyId,
      name: "",
      commodityId: null,
      locationId: null,
      counterpartyId: null,
      isLastDay: false,
      electricityType: undefined,
      components: [],
    } as PositionPricingStrategyModel;
  }

  function addPositionStrategy() {
    updatePositionStrategies([...(strategy.positionStrategies ?? []), createEmptyPositionStrategy()]);
  }

  function updatePositionStrategy(index: number, patch: Partial<PositionPricingStrategyModel>) {
    const updated = [...(strategy.positionStrategies ?? [])];
    updated[index] = {
      ...updated[index],
      ...patch,
    };
    updatePositionStrategies(updated);
  }

  function removePositionStrategy(index: number) {
    updatePositionStrategies((strategy.positionStrategies ?? []).filter((_, idx) => idx !== index));
  }

  function addPositionComponent(positionIndex: number) {
    const targetPosition = (strategy.positionStrategies ?? [])[positionIndex];
    const nextComponentId =
      (targetPosition.components ?? []).reduce((max, component) => Math.max(max, component.positionPricingComponentId), 0) + 1;

    const nextComponent: PositionPricingComponentModel = {
      positionPricingComponentId: nextComponentId,
      positionPricingStrategyId: targetPosition.positionPricingStrategyId,
      componentType: ComponentType.None,
      marketPriceProvider: undefined,
      marketViewProductId: null,
      settlementSymbol: null,
      bushelUnit: undefined,
      bushelVolume: null,
      contractUnit: undefined,
      contractVolume: null,
    };

    updatePositionStrategy(positionIndex, { components: [...(targetPosition.components ?? []), nextComponent] });
  }

  function updatePositionComponent(
    positionIndex: number,
    componentIndex: number,
    patch: Partial<PositionPricingComponentModel>,
  ) {
    const targetPosition = (strategy.positionStrategies ?? [])[positionIndex];
    const updatedComponents = [...(targetPosition.components ?? [])];
    updatedComponents[componentIndex] = {
      ...updatedComponents[componentIndex],
      ...patch,
    };

    updatePositionStrategy(positionIndex, { components: updatedComponents });
  }

  function removePositionComponent(positionIndex: number, componentIndex: number) {
    const targetPosition = (strategy.positionStrategies ?? [])[positionIndex];
    updatePositionStrategy(positionIndex, {
      components: (targetPosition.components ?? []).filter((_, idx) => idx !== componentIndex),
    });
  }

  return (
    <div>
      {intro}

      <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
        <div>
          <StoneXRow>
            <h4>Budget Strategy</h4>
          </StoneXRow>
          <div>
            <StoneXRow>
              <StoneXTextField
                label="Strategy Name"
                value={strategy.name}
                onUpdate={(value) => updateStrategy({ name: value })}
                fullWidth
              />
            </StoneXRow>

            <StrategyScopeFields
              customerData={customerData}
              commodityId={strategy.commodityId}
              locationId={strategy.locationId}
              counterpartyId={strategy.counterpartyId}
              showLocation={budgetByLocation}
              showCounterparty={budgetByCounterparty}
              onCommodityChange={(commodityId) => updateStrategy({ commodityId })}
              onLocationChange={(locationId) => updateStrategy({ locationId })}
              onCounterpartyChange={(counterpartyId) => updateStrategy({ counterpartyId })}
            />

            <StoneXSelect
              label="Electricity Type"
              options={Object.values(ElectricityType)}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={strategy.electricityType ?? null}
              onChange={(value) => updateStrategy({ electricityType: value ?? undefined })}
              clearable
              fullWidth
            />

            <br />
            <br />

            <ConversionFields
              values={{
                bushelUnit: strategy.bushelUnit,
                bushelVolume: strategy.bushelVolume,
                contractUnit: strategy.contractUnit,
                contractVolume: strategy.contractVolume,
              }}
              unitOptions={(customerData.units ?? []) as Unit[]}
              onChange={(patch) => updateStrategy(patch as Partial<BudgetPricingStrategyModel>)}
            />

            <StoneXRow align="baseline">
              <h4 style={{ marginBottom: 0 }}>Components</h4>
            </StoneXRow>

            {!hasComponents && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                This strategy has no price components, so budgets using it would have a volume
                and no price. Add at least one component.
              </Alert>
            )}

            {(strategy.components ?? []).map((component, componentIndex) => (
              <PricingComponentEditor
                key={`${component.budgetPricingComponentId}-${componentIndex}`}
                component={component}
                marketViewProducts={marketViewProducts}
                unitOptions={(customerData.units ?? []) as Unit[]}
                showSettlementField={false}
                showHedgeSource={true}
                removeLabel="Remove Component"
                onChange={(patch) => updateComponent(componentIndex, patch as Partial<BudgetPricingComponentModel>)}
                onRemove={() => removeComponent(componentIndex)}
              />
            ))}


            <Button className="pull-right" variant="text" onClick={addComponent}>Add Component</Button>
          </div>
        </div>
        {usesSeperatePricingStrategies &&
          <div>
            <StoneXRow align="baseline">
              <h4>Position Pricing Strategies</h4>
              <Button className="pull-right" variant="text" onClick={addPositionStrategy}>Add position pricing strategy</Button>
            </StoneXRow>
            <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
              {(strategy.positionStrategies ?? []).map((positionStrategy, positionIndex) => (
                <div key={`${positionStrategy.positionPricingStrategyId}-${positionIndex}`}>
                  <StoneXRow>
                    <StoneXTextField
                      label="Position Strategy Name"
                      value={positionStrategy.name}
                      onUpdate={(value) => updatePositionStrategy(positionIndex, { name: value })}
                      fullWidth
                    />
                  </StoneXRow>

                  <StrategyScopeFields
                    customerData={customerData}
                    commodityId={positionStrategy.commodityId}
                    locationId={positionStrategy.locationId}
                    counterpartyId={positionStrategy.counterpartyId}
                    showLocation={positionByLocation}
                    showCounterparty={positionByCounterparty}
                    onCommodityChange={(commodityId) => updatePositionStrategy(positionIndex, { commodityId })}
                    onLocationChange={(locationId) => updatePositionStrategy(positionIndex, { locationId })}
                    onCounterpartyChange={(counterpartyId) => updatePositionStrategy(positionIndex, { counterpartyId })}
                  />

                  <StoneXSelect
                    label="Electricity Type"
                    options={Object.values(ElectricityType)}
                    getId={(x) => x}
                    getOptionLabel={(x) => x}
                    value={positionStrategy.electricityType ?? null}
                    onChange={(value) => updatePositionStrategy(positionIndex, { electricityType: value ?? undefined })}
                    clearable
                    fullWidth
                  />

                  <br />
                  <br />

                  <StoneXRow align="baseline">
                    <h4 style={{ marginBottom: 0 }}>Position Components</h4>
                  </StoneXRow>

                  {(positionStrategy.components ?? []).map((positionComponent, componentIndex) => (
                    <PricingComponentEditor
                      key={`${positionComponent.positionPricingComponentId}-${componentIndex}`}
                      component={positionComponent}
                      marketViewProducts={marketViewProducts}
                      unitOptions={(customerData.units ?? []) as Unit[]}
                      showSettlementField={true}
                      showHedgeSource={false}
                      removeLabel="Remove Position Component"
                      onChange={(patch) =>
                        updatePositionComponent(positionIndex, componentIndex, patch as Partial<PositionPricingComponentModel>)
                      }
                      onRemove={() => removePositionComponent(positionIndex, componentIndex)}
                    />
                  ))}

                  <Button variant="text" onClick={() => addPositionComponent(positionIndex)}>
                    Add Position Component
                  </Button>
                  <Button variant="text" onClick={() => removePositionStrategy(positionIndex)}>Remove Position Strategy</Button>
                </div>
              ))}

            </div>

          </div>
        }
      </div>

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onCancel} disabled={isWorking}>Cancel</Button>
        <StoneXButton onClick={() => onSave(strategy)} isWorking={isWorking} disabled={!strategy.name?.trim()}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
